import React, { FC, useEffect } from "react";
import { Button } from "react-bootstrap";
import { UseFormReturn, useFieldArray } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { registerObject } from "go-form";
import FileUpload from "go-app/components/ImageForm/FileUpload";
import EmptyData from "go-core/components/EmptyData";
import { useConfirmation } from "go-form/components/ModalConfirm";
import { FileAttachmentApi } from "../../services/Api/Organization/types";
import AttachmentItemForm from "./AttachmentItemForm";

interface Props {
	form: UseFormReturn<any>;
}

const AttachmentsForm: FC<Props> = ({ form }) => {
	const { control, register, setValue } = form;
	const { t } = useTranslation();
	const confirmation = useConfirmation();
	const { append, remove, fields } = useFieldArray({
		keyName: "key",
		name: "file_attachments",
		control,
	});

	useEffect(() => {
		fields.forEach((field, index) => {
			registerFile(field as unknown as FileAttachmentApi, index);
		});
	}, [fields]);

	const getBase64 = (file: Blob) => {
		return new Promise((resolve, reject) => {
			const reader = new FileReader();
			reader.readAsDataURL(file);
			reader.onload = () => resolve(reader.result);
			reader.onerror = (error) => reject(error);
		});
	};

	const onRemoveFile = async (index: number) => {
		try {
			await confirmation({
				title: t("confirmation.title", { ns: "lib" }),
				message: t("confirmation.message.remove", { ns: "lib" }),
			});
			remove(index);
		} catch (err) {
			return null;
		}
	};

	const registerFile = (file: FileAttachmentApi, index: number) => {
		registerObject(register, `file_attachments.${index}`, ["name", "content", "id", "url"]);
		setValue(`file_attachments.${index}`, file);
	};

	const onUpdateFile = (file: string, file2: any) => {
		getBase64(file2).then((res: any) => {
			const createdFile = {
				name: file2.name,
				content: res.split(",")[1],
			} as FileAttachmentApi;
			append(createdFile);
		});
	};

	return (
		<div className="attachments" style={{ marginBottom: fields?.length > 0 ? "55px" : "80px" }}>
			{fields?.length > 0 ? (
				<>
					{fields.map((field, index) => (
						<>
							{index === 0 && <hr className="my-2" />}
							<AttachmentItemForm
								attachment={field as unknown as FileAttachmentApi}
								key={`attachment-${index}`}
								onRemoveFile={() => onRemoveFile(index)}
							/>
							<hr className="my-2" />
						</>
					))}
					<FileUpload
						fileIcon={
							<Button variant="add" className="mt-2">
								+ {t("modules.attachment.actions.add_attachment.title")}
							</Button>
						}
						onUpdateFile={onUpdateFile}
					/>
				</>
			) : (
				<EmptyData
					title=""
					description={t("modules.attachment.field.no_attachment.title")}
					content={
						<FileUpload
							fileIcon={
								<Button className="mt-2">
									+ {t("modules.attachment.actions.add_attachment.title")}
								</Button>
							}
							onUpdateFile={onUpdateFile}
						/>
					}
				/>
			)}
		</div>
	);
};

export default AttachmentsForm;
